import React, { useMemo, useState } from "react";
import styled, { keyframes } from "styled-components";
import { SendOutlined } from "@ant-design/icons";
import PaymentSecurityGroup from "../PaymentSecurityGroup";
import { CLUMSE_AI_DISPLAY_NAME } from "../../helpers/constants";
import { detectCurrentRegion } from "../../lib/regionDetection";
import {
  DEFAULT_REGION_LABEL,
  extractZipFromAddress,
  getMarketRegionFromZip,
} from "../../lib/regionFromZip";
import { device } from "../../lib/breakpoints";
import {
  CLUMSE_CHAT_WELCOME_MARKDOWN,
  formatClumseAiBubbleText,
} from "../../lib/clumseChatWelcome";
import { formatChatBoldSegments } from "../../lib/formatChatBoldSegments";

const DEMO_CHAT_INTRO = {
  role: "assistant",
  text: CLUMSE_CHAT_WELCOME_MARKDOWN,
};

const CHAT_BG = "#f0f4f9";
const BORDER = "#e8eaed";
const TEXT = "#202124";
const MUTED = "#5f6368";
const USER_BUBBLE = "#d3e3fd";
const ACTION = "#ff5722";

const CHECKOUT_STEPS = [
  {
    n: 1,
    text:
      "Pay the one-time search fee. Clumse AI invites vetted pros and talks with them directly so your scope is complete.",
  },
  {
    n: 2,
    text:
      "You return to this chat after payment. Clumse AI keeps working the vendors until bids are comparable.",
  },
  {
    n: 3,
    text:
      "You compare the strongest bids, usually within 1 to 3 business days, without chasing contractors yourself.",
  },
];

/** Default ZIP when we know the metro but not a street-level ZIP. */
const REGION_DEFAULT_ZIP = {
  "ST. LOUIS": "63122",
  CHICAGO: "60614",
  "NEW YORK": "10001",
  "LOS ANGELES": "90012",
  "SAN FRANCISCO": "94102",
  SEATTLE: "98101",
  BOSTON: "02108",
  DALLAS: "75201",
  ATLANTA: "30309",
  NATIONAL: "63122",
};

const REGION_CITY_LABEL = {
  "ST. LOUIS": "St. Louis",
  CHICAGO: "Chicago",
  "NEW YORK": "New York",
  "LOS ANGELES": "Los Angeles",
  "SAN FRANCISCO": "San Francisco",
  SEATTLE: "Seattle",
  BOSTON: "Boston",
  DALLAS: "Dallas",
  ATLANTA: "Atlanta",
};

const ZIP_PREFIX_CITY = {
  "972": "Portland",
  "503": "Portland",
  "787": "Austin",
  "733": "Austin",
  "802": "Denver",
  "372": "Nashville",
  "282": "Charlotte",
  "331": "Miami",
  "328": "Orlando",
};

function resolveDemoLocale(addressPreview) {
  const zipFromAddr = extractZipFromAddress(addressPreview || "");
  const region = detectCurrentRegion({
    address: addressPreview,
    zip: zipFromAddr || undefined,
  });
  const zip = zipFromAddr || REGION_DEFAULT_ZIP[region] || "63122";

  let cityLabel = getMarketRegionFromZip(zip);
  if (cityLabel === DEFAULT_REGION_LABEL) {
    const prefix3 = zip.slice(0, 3);
    cityLabel =
      REGION_CITY_LABEL[region] || ZIP_PREFIX_CITY[prefix3] || "your area";
  }

  return { zip, cityLabel };
}

function applyLocale(template, locale) {
  return String(template)
    .replace(/\{zip\}/g, locale.zip)
    .replace(/\{city\}/g, locale.cityLabel);
}

const SCENARIO_TEMPLATES = [
  {
    id: "minor",
    tabLabel: "Small fixes",
    threadTitle: "Dishwasher not draining",
    procurementTier: "MINOR",
    dispatchFee: 49,
    messages: [
      DEMO_CHAT_INTRO,
      {
        role: "user",
        text:
          "Our dishwasher will not drain. Water sits in the bottom after every cycle, and the dishes come out wet.",
      },
      {
        role: "assistant",
        text:
          "I can scope this as appliance repair. What ZIP is the home in, and is the unit built-in or portable?",
      },
      {
        role: "user",
        text: "{zip}. Built-in KitchenAid, about six years old.",
      },
      {
        role: "assistant",
        text:
          "Thanks. When did this start, and do you hear the drain pump run at the end of a cycle? Any error code on the display?",
      },
      {
        role: "user",
        text:
          "Started three days ago. I hear a hum but water stays. No error code. We run the garbage disposal first.",
      },
      {
        role: "assistant",
        text:
          "Good detail. Is the drain hose looped high under the sink, and is the sink drain slow when you run water?",
      },
      {
        role: "user",
        text:
          "Hose looks fine. Sink drains normally. I cleaned the filter yesterday but it did not help.",
      },
      {
        role: "assistant",
        text:
          "That points to a clogged filter screen, a stuck drain pump, or a blocked drain line. In {city}, most completed repairs for this land around $165 to $285 parts and labor. To start your match, there is a one-time flat $49 search fee. Once completed, I instantly package these diagnostic details into a clean project blueprint for our vetted local appliance pros so they can quote your job accurately without a wasted site visit. Do you prefer morning or afternoon visits?",
      },
      {
        role: "user",
        text:
          "Yes, please match someone who can come soon. Mornings work best.",
      },
      {
        role: "assistant",
        text:
          "Scope locked: Built-in KitchenAid dishwasher drain issue at {zip}, morning visits preferred. Click 'Start Bid Search' below to authorize the dispatch. I will work directly with our local network to bring you comparable bids!",
        finale: true,
      },
    ],
  },
  {
    id: "major",
    tabLabel: "Major systems",
    threadTitle: "AC not cooling upstairs",
    procurementTier: "MAJOR",
    dispatchFee: 49,
    messages: [
      DEMO_CHAT_INTRO,
      {
        role: "user",
        text:
          "AC is not cooling upstairs. Thermostat shows 78°F and it has been running all day. Downstairs feels fine.",
      },
      {
        role: "assistant",
        text:
          "Sounds like an HVAC cooling issue, possibly zoning or airflow. What ZIP is the home in, and how old is the system?",
      },
      {
        role: "user",
        text:
          "{zip}. Central air, about 12 years old. Two zones if that helps.",
      },
      {
        role: "assistant",
        text:
          "Helpful. When did upstairs stop cooling? Any ice on the outdoor unit, musty smell, or just weak airflow at the vents?",
      },
      {
        role: "user",
        text:
          "Started yesterday afternoon. Weak airflow upstairs, no ice, no smell. Outdoor unit runs but sounds normal.",
      },
      {
        role: "assistant",
        text:
          "Have you changed the HVAC filter in the last 90 days, and are all upstairs supply vents open?",
      },
      {
        role: "user",
        text:
          "Filter was changed last month. Vents are open. I checked the breaker and it did not trip.",
      },
      {
        role: "assistant",
        text:
          "Common causes here are a weak upstairs blower, a bad capacitor, low refrigerant, or a stuck zone damper. A tech should check pressures and airflow before quoting parts. In {city}, a service visit plus typical repairs for this pattern often total $450 to $1,150. The flat $49 search fee is one-time and separate. I will talk with HVAC contractors directly to fill gaps and align bids on the same scope.",
      },
      {
        role: "user",
        text: "Please line someone up. We have guests this weekend.",
      },
      {
        role: "assistant",
        text:
          "Scope locked: upstairs cooling issue at {zip}, urgent timeline noted. You are ready for vendor search. I will stay on the vendor threads until we have strong, apples-to-apples bids for you.",
        finale: true,
      },
    ],
  },
  {
    id: "capex",
    tabLabel: "Large projects",
    threadTitle: "Primary bath remodel",
    procurementTier: "CAPEX",
    dispatchFee: 49,
    messages: [
      DEMO_CHAT_INTRO,
      {
        role: "user",
        text:
          "We need a primary bath remodel: walk-in shower, tile, and a new vanity. Not sure how to scope it for bids.",
      },
      {
        role: "assistant",
        text:
          "I can frame this for contractors. What ZIP is the project in, and roughly how large is the bath?",
      },
      {
        role: "user",
        text:
          "{zip}. About 8 by 10 feet. We want a curbless walk-in shower and a double vanity.",
      },
      {
        role: "assistant",
        text:
          "Are you keeping the tub, moving any plumbing, or keeping fixtures where they are today?",
      },
      {
        role: "user",
        text:
          "Removing the tub. Walk-in shower on the tub wall. We need to move the vanity wall about two feet for the double sink.",
      },
      {
        role: "assistant",
        text:
          "Got it. What finishes are you leaning toward for tile and vanity, and when do you want work to start?",
      },
      {
        role: "user",
        text:
          "Large format porcelain tile, quartz top, brushed nickel fixtures. Hoping to start in 6 to 8 weeks.",
      },
      {
        role: "assistant",
        text:
          "Any photos of the current bath you can share later? Also, has this home had a bath remodel in the last 15 years?",
      },
      {
        role: "user",
        text:
          "I can upload photos tonight. Last remodel was 2004, so plumbing and electrical may need updates.",
      },
      {
        role: "assistant",
        text:
          "I will capture: demo tub, curbless shower with linear drain, wall move for double vanity, tile and fixture tier, and your start window for remodelers in {city}. In {city}, primary baths like this often run $24,000 to $42,000 once bids are in. Your one-time flat $49 search fee only starts outreach. I will talk with bath contractors directly so every bid reflects the same scope.",
      },
      {
        role: "user",
        text:
          "Perfect. Please invite contractors who do baths and handle the back and forth with them.",
      },
      {
        role: "assistant",
        text:
          "Bath remodel scope is ready for {zip}. You are ready for vendor search. I will work the contractors on details and photos so you get comparable bids, not allowance guesses.",
        finale: true,
      },
    ],
  },
];

function localizeScenarios(locale) {
  return SCENARIO_TEMPLATES.map((s) => ({
    ...s,
    messages: s.messages.map((m) => ({
      ...m,
      text: applyLocale(m.text, locale),
    })),
  }));
}

const TranscriptInner = styled.div`
  max-width: 720px;
  margin: 0 auto;
  padding: 14px 16px 12px;
  box-sizing: border-box;
`;

const MsgRow = styled.div`
  display: flex;
  width: 100%;
  gap: 12px;
  margin-bottom: 16px;
  flex-direction: row;
  align-items: flex-start;
  justify-content: ${(p) => (p.$user ? "flex-end" : "flex-start")};

  &:last-child {
    margin-bottom: 6px;
  }
`;

const Avatar = styled.div`
  width: 32px;
  height: 32px;
  flex-shrink: 0;
  border-radius: 50%;
  background: #fff;
  border: 2px solid #1e293b;
  display: flex;
  align-items: center;
  justify-content: center;
`;

const RailSpacer = styled.div`
  width: 32px;
  flex-shrink: 0;
`;

const AvatarImg = styled.img`
  width: 70%;
  height: 70%;
  object-fit: contain;
  filter: brightness(0);
`;

const AssistantStack = styled.div`
  min-width: 0;
  flex: 0 1 auto;
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  max-width: min(520px, 80%);
  margin-right: 10%;
`;

const UserStack = styled.div`
  display: flex;
  flex-direction: column;
  align-items: flex-end;
  flex-shrink: 0;
  max-width: min(520px, 80%);
  min-width: 0;
  margin-left: 10%;
`;

const Name = styled.span`
  font-size: 0.75rem;
  font-weight: 700;
  color: ${MUTED};
  line-height: 1.2;
  margin: 0 0 6px;
  align-self: ${(p) => (p.$user ? "flex-end" : "flex-start")};
`;

const Bubble = styled.div`
  min-width: 0;
  flex: 0 1 auto;
  width: ${(p) => (p.$user ? "max-content" : "100%")};
  max-width: 100%;
  padding: ${(p) => (p.$user ? "10px 16px" : "12px 16px")};
  border-radius: ${(p) =>
    p.$user ? "20px 20px 4px 20px" : "0 18px 18px 18px"};
  background: ${(p) => (p.$user ? USER_BUBBLE : "#fff")};
  border: 1px solid
    ${(p) => {
      if (p.$user) return "transparent";
      if (p.$finale) return "rgba(255, 87, 34, 0.35)";
      return BORDER;
    }};
  border-left: ${(p) => (p.$finale ? `3px solid ${ACTION}` : "none")};
  box-shadow: ${(p) =>
    p.$user
      ? "none"
      : p.$finale
      ? "0 1px 4px rgba(255, 87, 34, 0.12)"
      : "0 1px 2px rgba(60, 64, 67, 0.08)"};
  text-align: left;
`;

const BubbleText = styled.p`
  margin: 0;
  font-size: 0.9rem;
  line-height: 1.55;
  color: ${TEXT};
  text-align: left;
  white-space: pre-wrap;
  text-wrap: pretty;

  strong {
    font-weight: 700;
  }
`;

const DispatchRail = styled.div`
  flex-shrink: 0;
  padding: 10px 16px 12px;
  border-top: 1px solid ${BORDER};
  background: #fff;
  max-width: 720px;
  width: 100%;
  margin: 0 auto;
  box-sizing: border-box;

  @media ${device.mobile} {
    padding: 8px 12px 10px;
  }
`;

const BidsSearchExplainer = styled.div`
  margin-bottom: 10px;

  @media ${device.mobile} {
    margin-bottom: 8px;
    text-align: left;
  }
`;

const BidsSearchExplainerTitle = styled.div`
  font-size: 0.7rem;
  font-weight: 700;
  letter-spacing: 0.05em;
  text-transform: uppercase;
  color: #3c4043;
  margin-bottom: 8px;

  @media ${device.mobile} {
    text-align: left;
  }
`;

const BidsSearchExplainerSteps = styled.div`
  display: flex;
  flex-direction: column;
  gap: 7px;
  align-items: stretch;

  @media ${device.mobile} {
    gap: 6px;
    align-items: flex-start;
  }
`;

const BidsSearchExplainerStep = styled.div`
  display: flex;
  align-items: flex-start;
  gap: 8px;
  width: 100%;
  text-align: left;

  @media ${device.mobile} {
    justify-content: flex-start;
  }
`;

const BidsSearchExplainerStepNum = styled.span`
  flex-shrink: 0;
  width: 18px;
  height: 18px;
  margin-top: 1px;
  border-radius: 50%;
  background: rgba(255, 87, 34, 0.12);
  color: var(--color-action, #ff5722);
  font-size: 0.65rem;
  font-weight: 700;
  line-height: 18px;
  text-align: center;
`;

const BidsSearchExplainerStepText = styled.span`
  flex: 1;
  min-width: 0;
  font-size: 0.74rem;
  line-height: 1.4;
  color: #5f6368;
  text-align: left;
  text-wrap: pretty;
`;

/**
 * @param {{ addressPreview?: string | null }} props
 */
export default function HomeHeroChatDemo({ addressPreview = null }) {
  const locale = useMemo(() => resolveDemoLocale(addressPreview), [
    addressPreview,
  ]);
  const scenarios = useMemo(() => localizeScenarios(locale), [locale]);

  const [activeId, setActiveId] = useState(SCENARIO_TEMPLATES[0].id);
  const scenario = scenarios.find((s) => s.id === activeId) ?? scenarios[0];

  return (
    <Root aria-label="Example Clumse AI conversations by job type">
      <ScenarioStrip role="tablist" aria-label="Example job types">
        {scenarios.map((s) => (
          <ScenarioTab
            key={s.id}
            type="button"
            role="tab"
            aria-selected={s.id === activeId}
            $selected={s.id === activeId}
            onClick={() => setActiveId(s.id)}
          >
            {s.tabLabel}
          </ScenarioTab>
        ))}
      </ScenarioStrip>

      <ChatShell>
        <ChatHeader>
          <ChatHeaderTitle>{scenario.threadTitle}</ChatHeaderTitle>
          {locale.cityLabel !== "your area" ? (
            <ChatHeaderMeta>
              Example for {locale.cityLabel} ({locale.zip})
            </ChatHeaderMeta>
          ) : (
            <ChatHeaderMeta>Example for ZIP {locale.zip}</ChatHeaderMeta>
          )}
        </ChatHeader>

        <Transcript key={`${scenario.id}-${locale.zip}`}>
          <TranscriptInner>
            {scenario.messages.map((msg, i) => {
              const isUser = msg.role === "user";
              const isAssistant = msg.role === "assistant";
              const prev = scenario.messages[i - 1];
              const showAvatar =
                isAssistant && (i === 0 || prev?.role !== "assistant");
              const showUserLabel =
                isUser && (i === 0 || prev?.role !== "user");

              if (isUser) {
                return (
                  <MsgRow key={i} $user>
                    <UserStack>
                      {showUserLabel ? <Name $user>You</Name> : null}
                      <Bubble $user>
                        <BubbleText>{msg.text}</BubbleText>
                      </Bubble>
                    </UserStack>
                  </MsgRow>
                );
              }

              return (
                <MsgRow key={i}>
                  {showAvatar ? (
                    <Avatar aria-hidden>
                      <AvatarImg src="/logo.png" alt="" />
                    </Avatar>
                  ) : (
                    <RailSpacer aria-hidden />
                  )}
                  <AssistantStack>
                    {showAvatar ? <Name>{CLUMSE_AI_DISPLAY_NAME}</Name> : null}
                    <Bubble $finale={msg.finale}>
                      <BubbleText>
                        {formatChatBoldSegments(
                          formatClumseAiBubbleText(msg.text)
                        )}
                      </BubbleText>
                    </Bubble>
                  </AssistantStack>
                </MsgRow>
              );
            })}
          </TranscriptInner>
        </Transcript>

        <DispatchRail aria-hidden>
          <BidsSearchExplainer>
            <BidsSearchExplainerTitle>
              What happens next
            </BidsSearchExplainerTitle>
            <BidsSearchExplainerSteps>
              {CHECKOUT_STEPS.map((step) => (
                <BidsSearchExplainerStep key={step.n}>
                  <BidsSearchExplainerStepNum aria-hidden>
                    {step.n}
                  </BidsSearchExplainerStepNum>
                  <BidsSearchExplainerStepText>
                    {step.text}
                  </BidsSearchExplainerStepText>
                </BidsSearchExplainerStep>
              ))}
            </BidsSearchExplainerSteps>
          </BidsSearchExplainer>
          <PaymentSecurityGroup
            onDispatch={() => undefined}
            dispatchFee={scenario.dispatchFee}
            autoDetectedTier={scenario.procurementTier}
            showTierSelector={false}
            density="comfortable"
            trustVariant="none"
          />
        </DispatchRail>

        <Composer aria-hidden>
          <ComposerField>Describe your project...</ComposerField>
          <SendBtn type="button" tabIndex={-1}>
            <SendOutlined />
          </SendBtn>
        </Composer>
      </ChatShell>

      <Footnote>Example (not a live chat)</Footnote>
    </Root>
  );
}

const fadeIn = keyframes`
  from { opacity: 0; }
  to { opacity: 1; }
`;

const Root = styled.div`
  width: 100%;
  display: flex;
  flex-direction: column;
  gap: 0.6rem;
`;

const ScenarioStrip = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 0.4rem;
  justify-content: center;
  width: 100%;
`;

const ScenarioTab = styled.button`
  appearance: none;
  border: 1px solid
    ${(p) =>
      p.$selected ? "rgba(255, 87, 34, 0.65)" : "rgba(148, 163, 184, 0.3)"};
  background: ${(p) =>
    p.$selected ? "rgba(255, 87, 34, 0.14)" : "transparent"};
  color: ${(p) => (p.$selected ? "#fff" : "#cbd5e1")};
  border-radius: 999px;
  padding: 0.4rem 0.85rem;
  font-size: 0.8rem;
  font-weight: ${(p) => (p.$selected ? 700 : 600)};
  cursor: pointer;
  transition: border-color 0.15s ease, background 0.15s ease;

  &:focus-visible {
    outline: 2px solid ${ACTION};
    outline-offset: 2px;
  }
`;

const ChatShell = styled.div`
  display: flex;
  flex-direction: column;
  max-height: min(48rem, 82vh);
  background: ${CHAT_BG};
  border: 2px solid ${ACTION};
  border-radius: 14px;
  overflow: hidden;
  box-shadow: 0 10px 32px rgba(0, 0, 0, 0.35), 0 0 0 1px rgba(255, 87, 34, 0.15),
    0 4px 20px rgba(255, 87, 34, 0.22);

  @media (min-width: 1024px) {
    max-height: 44rem;
  }

  @media (max-width: 768px) {
    max-height: min(46rem, 78vh);
  }
`;

const ChatHeader = styled.div`
  flex-shrink: 0;
  padding: 18px 14px 10px;
  border-bottom: 1px solid ${BORDER};
  background: #fff;
  text-align: center;
`;

const ChatHeaderTitle = styled.h2`
  margin: 0;
  font-size: 1rem;
  font-weight: 700;
  color: ${TEXT};
  text-align: center;
  letter-spacing: -0.02em;
  line-height: 1.3;
  text-transform: uppercase;
`;

const ChatHeaderMeta = styled.p`
  margin: 6px 0 0;
  font-size: 0.72rem;
  font-weight: 600;
  color: ${MUTED};
  text-align: center;
  line-height: 1.35;
`;

const Transcript = styled.div`
  flex: 1 1 auto;
  min-height: 0;
  max-height: 24rem;
  overflow-y: scroll;
  overflow-x: hidden;
  scrollbar-gutter: stable;
  scrollbar-width: thin;
  scrollbar-color: #9aa0a6 #e8eaed;
  -webkit-overflow-scrolling: touch;
  animation: ${fadeIn} 0.25s ease;

  &::-webkit-scrollbar {
    width: 8px;
  }

  &::-webkit-scrollbar-track {
    background: #e8eaed;
  }

  &::-webkit-scrollbar-thumb {
    background: #9aa0a6;
    border-radius: 4px;

    &:hover {
      background: #5f6368;
    }
  }

  @media (min-width: 1024px) {
    max-height: 22rem;
  }
`;

const Composer = styled.div`
  flex-shrink: 0;
  display: flex;
  align-items: stretch;
  gap: 10px;
  padding: 10px 12px 12px;
  border-top: 1px solid ${BORDER};
  background: #fff;
`;

const ComposerField = styled.div`
  flex: 1;
  display: flex;
  align-items: center;
  padding: 0 12px;
  min-height: 44px;
  font-size: 0.875rem;
  color: #80868b;
  text-align: left;
  background: #fff;
  border: 1px solid #dadce0;
  border-radius: 10px;
`;

const SendBtn = styled.button`
  width: 44px;
  flex-shrink: 0;
  border: none;
  border-radius: 10px;
  background: ${ACTION};
  color: #fff;
  display: flex;
  align-items: center;
  justify-content: center;
  opacity: 0.5;
  cursor: default;
`;

const Footnote = styled.p`
  margin: 0;
  text-align: center;
  font-size: 0.65rem;
  font-weight: 600;
  letter-spacing: 0.05em;
  text-transform: uppercase;
  color: rgba(148, 163, 184, 0.75);
`;
